import { useState } from 'react';

import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { ReactComponent as BackrsFilledDropShadow } from '@backrs_ui/common-ui/src/assets/images/backrs-filled-drop-shadow.svg';
import { BackrsForm } from '@backrs_ui/common-ui/src/molecules/BackrsForm/BackrsForm';
import {
  BackrsFormField,
  BackrsFormValues,
} from '@backrs_ui/common-ui/src/molecules/BackrsForm/types';

import { Box, Typography, Theme, SxProps, ButtonBase, useMediaQuery } from '@mui/material';
import { useForm } from 'react-hook-form';

import { AnimatedHeight } from './AnimatedHeight';
import { LoginCarousel } from './LoginCarousel';
import { LoginMethod, LoginMethodTabs } from './LoginMethodTabs';
import { OauthButtons } from './OauthButtons';
import { ForgotPasswordPopup } from '../BackrsForm/ForgotPasswordPopup';

const LOGIN_STRINGS = appStrings.modules.login;

type LoginPageProps = Readonly<{
  title: string;
  handleLoggingIn: (phone: string, email: string, password: string) => void;
  handleSigningUp?: () => void;
  errorMessage?: string;
  customAction?: {
    text: string;
    onClick: () => void;
  };
  environmentUrl: string;
  source: string;
  // Present ⇒ OAuth is enabled and both creds exist. Modelling the three as one
  // optional object makes "enabled but missing creds" unrepresentable at this boundary.
  oauth?: Readonly<{ stytchHost: string; stytchPublicToken: string }>;
}>;

const defaultLoginValues: BackrsFormValues = {
  phone: '',
  email: '',
  password: '',
};

export function LoginPage({
  title,
  handleLoggingIn,
  handleSigningUp,
  errorMessage,
  environmentUrl,
  source,
  oauth,
}: LoginPageProps) {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);
  // Device-derived default is seeded once at mount on purpose: a mid-session resize
  // must NOT switch tabs, or it would wipe an in-progress form.
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const [loginMethod, setLoginMethod] = useState<LoginMethod>(isMobile ? 'phone' : 'email');

  const methods = useForm<BackrsFormValues>({
    defaultValues: defaultLoginValues,
    mode: 'onSubmit',
  });

  // Each tab owns exactly one login pathway, so we swap the rendered fields rather
  // than disabling the inactive ones. handleLoggingIn routes on `phone` truthiness,
  // so the inactive field's value MUST be cleared on switch or a stale phone/email
  // would hijack the request.
  const handleMethodChange = (method: LoginMethod) => {
    if (method === loginMethod) {
      return;
    }

    setLoginMethod(method);
    methods.clearErrors();

    if (method === 'phone') {
      methods.setValue('email', '');
      methods.setValue('password', '');
    } else {
      methods.setValue('phone', '');
    }
  };

  const phoneField: BackrsFormField = {
    id: 'phone',
    name: 'phone',
    placeholder: LOGIN_STRINGS.phonePlaceholder,
    type: 'tel',
    autoComplete: 'tel',
    autoFocus: true,
    rules: {
      required: LOGIN_STRINGS.error.phoneRequired,
      pattern: {
        value: /^\d{10}$/,
        message: LOGIN_STRINGS.error.invalidPhone,
      },
    },
  };

  const emailField: BackrsFormField = {
    id: 'email',
    name: 'email',
    placeholder: LOGIN_STRINGS.emailPlaceholder,
    type: 'text',
    autoComplete: 'email',
    autoFocus: true,
    rules: {
      required: LOGIN_STRINGS.error.emailRequired,
      pattern: {
        value: /^\S+@\S+\.\S+$/,
        message: LOGIN_STRINGS.error.invalidEmail,
      },
    },
  };

  const passwordField: BackrsFormField = {
    id: 'password',
    name: 'password',
    placeholder: LOGIN_STRINGS.passwordPlaceholder,
    type: isPasswordVisible ? 'text' : 'password',
    autoComplete: 'current-password',
    rules: {
      required: LOGIN_STRINGS.error.passwordRequired,
    },
    icon: {
      type: 'RIGHT_ICON',
      rightIcon: isPasswordVisible ? 'EyeOff' : 'Eye',
      onRightIconClick: () => {
        setIsPasswordVisible((prev) => !prev);

        setTimeout(() => {
          setIsPasswordVisible(false);
        }, 5000);
      },
    },
  };

  const fields: BackrsFormField[] =
    loginMethod === 'phone' ? [phoneField] : [emailField, passwordField];

  const handleSubmission = (data: BackrsFormValues) => {
    handleLoggingIn(data.phone, data.email, data.password);
  };

  return (
    <Box sx={componentContainerSx}>
      <Box sx={containerStyle}>
        <Box sx={loginFormStyle}>
          <Box sx={responsiveBackrsLogoStyle}>
            <BackrsFilledDropShadow width="48px" height="48px" />
          </Box>
          <Box sx={textContainerSx}>
            <Typography
              variant="display-xs-semibold"
              color={(theme) => theme.backrs.colors['Colors/Text/text-primary']}
            >
              {title}
            </Typography>
            <Typography
              variant="text-md-regular"
              color={(theme) => theme.backrs.colors['Colors/Text/text-tertiary']}
              sx={{ textAlign: 'center' }}
            >
              {LOGIN_STRINGS.welcomeBack}
            </Typography>
          </Box>
          <LoginMethodTabs value={loginMethod} onChange={handleMethodChange} />
          <AnimatedHeight>
            <BackrsForm
              fields={fields}
              methods={methods}
              onSubmit={handleSubmission}
              buttonLabel={appStrings.buttonText.signIn}
              buttonId="login-button"
            >
              <Box sx={forgotPasswordWrapSx(loginMethod === 'email')}>
                <ButtonBase
                  id="forgot-password-button"
                  data-ph-capture-attribute-name="forgot-password-button"
                  sx={forgotPasswordContainerStyle}
                  onClick={() => setIsForgotPasswordOpen(true)}
                  disabled={loginMethod !== 'email'}
                  disableRipple
                >
                  <Typography variant="text-sm-semibold" sx={forgotPasswordStyle}>
                    {LOGIN_STRINGS.forgotPassword}
                  </Typography>
                </ButtonBase>
              </Box>
            </BackrsForm>
          </AnimatedHeight>
          {oauth && (
            <Box sx={oauthSectionSx}>
              <OauthButtons {...oauth} />
            </Box>
          )}
          <ForgotPasswordPopup
            isOpen={isForgotPasswordOpen}
            setIsOpen={setIsForgotPasswordOpen}
            environmentUrl={environmentUrl}
            source={source}
          />
          {errorMessage && <Box sx={errorMessageSx}>{errorMessage}</Box>}
          {handleSigningUp && (
            <Box sx={handleSignUpSx}>
              <Typography component="span" variant="text-sm-regular">
                {LOGIN_STRINGS.dontHaveAccount}
              </Typography>{' '}
              <Typography
                id="signup-button"
                data-ph-capture-attribute-name="signup-button"
                component="span"
                variant="text-sm-regular"
                sx={embeddedButtonSx}
                onClick={handleSigningUp}
              >
                {LOGIN_STRINGS.signUp}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={carouselContainerSx}>
        <LoginCarousel />
      </Box>
    </Box>
  );
}

// Hidden state goes absolute so the row leaves the flow instantly — the outer
// AnimatedHeight then eases the height down once, while opacity fades over the same
// 250ms. No nested height measurement, so nothing stutters. `pointerEvents: none`
// (plus the button's own `disabled`) keeps the invisible row inert.
const forgotPasswordWrapSx =
  (isVisible: boolean): SxProps<Theme> =>
  () => ({
    position: isVisible ? 'static' : 'absolute',
    width: '100%',
    opacity: isVisible ? 1 : 0,
    pointerEvents: isVisible ? 'auto' : 'none',
    transition: 'opacity 250ms ease',
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
  });

const errorMessageSx: SxProps<Theme> = ({ backrs }) => ({
  mt: 2,
  color: backrs.colors['Colors/Text/text-error-primary'],
  '@media (min-width: 900px) and (max-height: 620px)': {
    mt: '-2%',
    mb: '-5%',
  },
});

const handleSignUpSx: SxProps<Theme> = ({ backrs }) => ({
  paddingBottom: backrs.spacing['spacing-xl'],
});

const embeddedButtonSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'inline',
  color: backrs.colors['Colors/Text/text-brand-tertiary'],
  cursor: 'pointer',
});

const componentContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    md: '1fr 1fr',
  },
  color: backrs.colors['Yellow/950'],
  height: '100dvh',
  overflow: 'hidden',
});

const carouselContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: {
    xs: 'none',
    md: 'block',
  },
  backgroundColor: backrs.colors['Colors/Foreground/fg-brand-secondary'],
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  alignSelf: 'center',
  width: '100%',
  height: '100dvh',
  borderLeft: `1px solid ${backrs.colors['Colors/Base/Black']}`,
});

const containerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  margin: '0 auto',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: {
    xs: 'flex-start',
    md: 'center',
  },
  alignSelf: 'center',
  justifySelf: 'center',
  px: {
    xs: backrs.spacing['spacing-lg'],
    sm: backrs.spacing['spacing-2xl'],
  },
  backgroundColor: backrs.colors['Beige/50'],
  height: '100dvh',
  width: '100%',
  py: {
    xs: backrs.spacing['spacing-6xl'],
    md: backrs.spacing['spacing-4xl'],
  },
});

const loginFormStyle: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: {
    xs: backrs.colors['Beige/50'],
    sm: backrs.colors['Colors/Base/White'],
  },
  paddingX: {
    xs: backrs.spacing['spacing-md'],
    sm: backrs.spacing['spacing-2xl'],
  },
  borderRadius: {
    xs: 0,
    sm: backrs.radius['radius-md'],
  },
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  maxWidth: {
    xs: '100%',
    sm: '440px',
  },
  width: '100%',
  boxSizing: 'border-box',
  '@media (min-width: 300px)': {
    paddingX: backrs.spacing['spacing-xl'],
  },
  '@media (min-width: 600px)': {
    paddingX: backrs.spacing['spacing-4xl'],
  },
  border: {
    xs: 'none',
    sm: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  },
  margin: {
    xs: 0,
    sm: `auto ${backrs.spacing['spacing-4xl']}`,
    md: `auto ${backrs.spacing['spacing-4xl']}`,
    lg: `auto ${backrs.spacing['spacing-6xl']}`,
  },
  gap: backrs.spacing['spacing-3xl'],
  pt: backrs.spacing['spacing-2xl'],
  pb: backrs.spacing['spacing-2xl'],
});

const responsiveBackrsLogoStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  maxWidth: '48px',
  maxHeight: '48px',
  mt: backrs.spacing['spacing-3xl'],
});

const forgotPasswordContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'block',
  ml: 'auto',
  mr: backrs.spacing['spacing-md'],
});

const forgotPasswordStyle: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Brand/500'],
});

const textContainerSx: SxProps<Theme> = (theme: Theme) => ({
  ...loginFormStyle(theme),
  pt: 0,
  pb: 0,
  mt: '4px',
  gap: '.5rem',
  outline: 'none',
  border: 'none',
  boxShadow: 'none',
});

const oauthSectionSx: SxProps<Theme> = ({ backrs }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
});
